import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Modal from "../Common/Modal";
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useTranslation } from '../../utils/translations';
import { toast } from "sonner"
import { useAuth } from '../../context/AuthContext';

const { FiCopy, FiCheck, FiX, FiEye } = FiIcons;

const EmbedCodeModal = ({ widget, onClose }) => {
  const { user } = useAuth();
  const t = useTranslation(user?.language);
  const [copied, setCopied] = useState(false);
  
  const getEmbedCode = () => {
    // Replace with your actual app URL in production
    const appUrl = window.location.origin;
    
    return `<script>(function(){var s=document.createElement('script');s.src='${appUrl}/widget.js?t=${widget.public_token}';s.async=true;s.crossOrigin="anonymous";document.head.appendChild(s);}());</script>`;
  };
  
  const handleCopyCode = () => {
    navigator.clipboard.writeText(getEmbedCode());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success(t('widgets.embedCode.copySuccess', 'Widget embed code copied to clipboard', 'Code d\'intégration du widget copié dans le presse-papiers'));
  };

  const handlePreview = () => {
    const previewWindow = window.open('', '_blank');
    const previewHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${t('widgets.preview.title', 'Widget Preview')}</title>
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
          <style>
            html, body { height: 100%; }
            body { display: flex; flex-direction: column; margin: 0; font-family: 'Inter', sans-serif; }
            main { flex: 1 0 auto; }
            footer { flex-shrink: 0; }
          </style>
          <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap" rel="stylesheet">
        </head>
        <body class="bg-gray-50">
          <!-- Header -->
          <header class="bg-indigo-700 text-white shadow-lg">
            <div class="container mx-auto px-4 py-4 flex justify-between items-center">
              <h1 class="text-2xl font-bold tracking-tight">${t('widgets.preview.headerTitle', 'Sample Website')}</h1>
              <nav>
                <ul class="flex space-x-6">
                  <li><a href="#" class="hover:text-indigo-200 transition-colors">${t('widgets.preview.navHome', 'Home')}</a></li>
                  <li><a href="#" class="hover:text-indigo-200 transition-colors">${t('widgets.preview.navAbout', 'About')}</a></li>
                  <li><a href="#" class="hover:text-indigo-200 transition-colors">${t('widgets.preview.navServices', 'Services')}</a></li>
                  <li><a href="#" class="hover:text-indigo-200 transition-colors">${t('widgets.preview.navContact', 'Contact')}</a></li>
                </ul>
              </nav>
            </div>
          </header>

          <!-- Main Content -->
          <main class="container mx-auto px-4 py-12">
            <section class="bg-white rounded-xl shadow-lg p-8 max-w-4xl mx-auto">
              <h2 class="text-3xl font-semibold text-gray-800 mb-6">${t('widgets.preview.mainTitle', 'Welcome to Our Website')}</h2>
              <p class="text-gray-600 leading-relaxed mb-6">
                ${t('widgets.preview.mainContent', 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.')}
              </p>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div class="bg-gray-50 p-6 rounded-lg shadow-sm">
                  <h3 class="text-xl font-semibold text-gray-800 mb-3">${t('widgets.preview.featureOneTitle', 'Feature One')}</h3>
                  <p class="text-gray-600">${t('widgets.preview.featureOneContent', 'Discover our innovative solutions designed to help you achieve your goals efficiently and effectively.')}</p>
                </div>
                <div class="bg-gray-50 p-6 rounded-lg shadow-sm">
                  <h3 class="text-xl font-semibold text-gray-800 mb-3">${t('widgets.preview.featureTwoTitle', 'Feature Two')}</h3>
                  <p class="text-gray-600">${t('widgets.preview.featureTwoContent', 'Experience seamless integration with our cutting-edge technology for a smooth user experience.', 'Profitez d\'une intégration fluide avec notre technologie de pointe pour une expérience utilisateur optimale.')}</p>
                </div>
              </div>
            </section>
          </main>

          <!-- Footer -->
          <footer class="bg-gray-900 text-white py-6">
            <div class="container mx-auto px-4 text-center">
              <p class="text-sm">${t('widgets.preview.footerText', '&copy; 2025 Sample Website. All rights reserved.')}</p>
              <div class="mt-2">
                <a href="#" class="text-gray-400 hover:text-white mx-2">${t('widgets.preview.footerPrivacy', 'Privacy Policy')}</a>
                <a href="#" class="text-gray-400 hover:text-white mx-2">${t('widgets.preview.footerTerms', 'Terms of Service')}</a>
              </div>
            </div>
          </footer>

          ${getEmbedCode()}
        </body>
      </html>
    `;
    previewWindow.document.write(previewHtml);
    previewWindow.document.close();
  };

  return (
    <Modal isOpen={true} title={t('widgets.embedCode.title', 'Embed Widget: {name}').replace('{name}', widget.name)} onClose={onClose}>
      <p className="text-gray-700 dark:text-gray-300 mb-4" dangerouslySetInnerHTML={{ __html: t('widgets.embedCode.description', 'Copy this code and paste it into your website\'s HTML, just before the closing <code>&lt;/body&gt;</code> tag.', 'Copiez ce code et collez-le dans le HTML de votre site web, juste avant la balise de fermeture <code>&lt;/body&gt;</code>.') }} />

      <div className="bg-gray-50 dark:bg-gray-750 rounded-lg p-4 relative">
        <pre className="text-sm text-gray-800 dark:text-gray-500 whitespace-pre-wrap overflow-x-auto">
          {getEmbedCode()}
        </pre>
        
        <div className="absolute top-2 right-2 flex space-x-2">
          <button
            onClick={handlePreview}
            className="p-2 bg-white dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
            title={t('widgets.embedCode.preview', 'Preview Widget')}
          >
            <SafeIcon 
              icon={FiEye} 
              className="w-5 h-5 text-gray-400" 
            />
          </button>
          <button
            onClick={handleCopyCode}
            className="p-2 bg-white dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
            title={copied ? t('widgets.embedCode.copied', 'Copied!') : t('widgets.embedCode.copyToClipboard', 'Copy to clipboard')}
          >
            <SafeIcon 
              icon={copied ? FiCheck : FiCopy} 
              className={`w-5 h-5 ${copied ? 'text-green-500' : 'text-gray-400'}`} 
            />
          </button>
        </div>
      </div>
      
      <div className="mt-6">
        <h4 className="font-medium text-gray-900 dark:text-white mb-2">{t('widgets.embedCode.installationNotes', 'Installation Notes:', 'Notes d\'installation :')}</h4>
        <ul className="list-disc pl-5 text-sm text-gray-700 dark:text-gray-300 space-y-1">
          <li>{t('widgets.embedCode.positionNote', 'The widget will appear as a chat button in the {position} corner of your website.').replace('{position}', widget.ui?.position.replace('-', ' '))}</li>
          <li>{t('widgets.embedCode.interactionNote', 'Users can click on the button to open the chat interface.', 'Les utilisateurs peuvent cliquer sur le bouton pour ouvrir l\'interface de chat.')}</li>
          <li>{t('widgets.embedCode.autoOpenNote', 'The widget will automatically open after {delay} seconds if enabled.', 'Le widget s\'ouvrira automatiquement après {delay} secondes si activé.').replace('{delay}', widget.ui?.autoOpenDelay / 1000)}</li>
          {widget.allowed_origins && widget.allowed_origins.length > 0 && (
            <li>
              <strong>{t('widgets.embedCode.important', 'Important:')}</strong> {t('widgets.embedCode.allowedOriginsNote', 'This widget will only work on the following domains:')}
              <ul className="pl-5 mt-1">
                {widget.allowed_origins.map((origin, i) => (
                  <li key={i} className="text-blue-600 dark:text-blue-400">{origin}</li>
                ))}
              </ul>
            </li>
          )}
        </ul>
      </div>        
    </Modal>
  );
};

export default EmbedCodeModal;